import { useState, useEffect } from "react";
import {
  Users,
  Mail,
  Building2,
  Briefcase,
  Calendar,
  Search,
  Filter,
  RefreshCw,
  CheckCircle2,
  Clock,
  XCircle,
  MessageSquare,
  ExternalLink,
  ChevronDown,
  TrendingUp,
  Target,
  User,
  Landmark,
  FileText,
  Copy
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  new: { label: "New", color: "bg-blue-100 text-blue-700 border-blue-200", icon: Clock },
  contacted: { label: "Contacted", color: "bg-amber-100 text-amber-700 border-amber-200", icon: MessageSquare },
  meeting_scheduled: { label: "Meeting Scheduled", color: "bg-violet-100 text-violet-700 border-violet-200", icon: Calendar },
  in_discussions: { label: "In Discussions", color: "bg-cyan-100 text-cyan-700 border-cyan-200", icon: Users },
  closed_won: { label: "Closed - Invested", color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  closed_lost: { label: "Closed - No Interest", color: "bg-slate-100 text-slate-600 border-slate-200", icon: XCircle }
};

const investorTypeLabels = {
  angel: "Angel Investor",
  vc: "Venture Capital",
  family_office: "Family Office",
  corporate: "Corporate / Strategic",
  accelerator: "Accelerator / Incubator",
  advisor: "Advisor / Consultant",
  potential_customer: "Potential Customer",
  press: "Press / Media",
  other: "Other"
};

const PUBLIC_BASE = (process.env.REACT_APP_PUBLIC_URL || "https://sterilemate.com").replace(/\/$/, "");

const QUICK_LINKS = [
  {
    key: "ronan",
    label: "Plan C \u2014 16651 Ronan Rd (Frost Bank)",
    desc: "Direct, no-password Frost Bank link \u2014 auto-loads Plan C.",
    href: "/investor/frost-bank",
    accent: "bg-blue-50 border-blue-200 text-blue-900",
    chip: "bg-blue-100 text-blue-700",
    icon: Landmark,
  },
  {
    key: "ronan-letter",
    label: "Frost Bank Cover Letter",
    desc: "Printable SBA introduction letter \u2014 Tina Poindexter & Michael Cruz.",
    href: "/investor/frost-bank/letter",
    accent: "bg-indigo-50 border-indigo-200 text-indigo-900",
    chip: "bg-indigo-100 text-indigo-700",
    icon: FileText,
  },
  {
    key: "humble",
    label: "Plan A \u2014 Humble HQ",
    desc: "Admin direct entry, no gate. Loads Plan A scenario.",
    href: "/investor?bypass=1&scenario=humble",
    accent: "bg-emerald-50 border-emerald-200 text-emerald-900",
    chip: "bg-emerald-100 text-emerald-700",
    icon: Building2,
  },
  {
    key: "northsam",
    label: "Plan B \u2014 2SIXTY3 / N. Sam Houston",
    desc: "Admin direct entry, no gate. Loads Plan B scenario.",
    href: "/investor?bypass=1&scenario=northsam",
    accent: "bg-amber-50 border-amber-200 text-amber-900",
    chip: "bg-amber-100 text-amber-700",
    icon: Building2,
  },
];

function QuickAccessPanel() {
  const copy = async (path) => {
    try {
      await navigator.clipboard.writeText(`${PUBLIC_BASE}${path}`);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Unable to copy link");
    }
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="investor-quick-access-panel">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <ExternalLink className="w-4 h-4 text-blue-600" />
            Quick Access &mdash; Investor Portal &amp; Bank Share Links
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Open the investor portal directly (no form), or copy a shareable {PUBLIC_BASE} link to send to bankers/partners.
          </p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {QUICK_LINKS.map((l) => {
          const Icon = l.icon;
          return (
            <div key={l.key} className={`rounded-lg border p-3 ${l.accent}`} data-testid={`quick-link-${l.key}`}>
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-md ${l.chip} flex items-center justify-center shrink-0`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold leading-tight">{l.label}</p>
                  <p className="text-[11px] text-slate-600 mt-0.5">{l.desc}</p>
                  <p className="text-[11px] font-mono text-slate-500 mt-1 break-all">{PUBLIC_BASE}{l.href}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <a
                      href={l.href}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-slate-900 hover:bg-slate-700 text-white text-[11px] font-semibold"
                      data-testid={`quick-link-open-${l.key}`}
                    >
                      <ExternalLink className="w-3 h-3" />
                      Open
                    </a>
                    <button
                      type="button"
                      onClick={() => copy(l.href)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white hover:bg-slate-100 border border-slate-300 text-slate-700 text-[11px] font-semibold"
                      data-testid={`quick-link-copy-${l.key}`}
                    >
                      <Copy className="w-3 h-3" />
                      Copy link
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


export default function InvestorLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [expandedLead, setExpandedLead] = useState(null);
  const [editingNotes, setEditingNotes] = useState({});

  useEffect(() => {
    fetchLeads();
  }, [filterStatus]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("sterilemate_token");
      const url = filterStatus 
        ? `${API}/investor-leads?status=${filterStatus}` 
        : `${API}/investor-leads`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error("Failed to fetch leads");
      
      const data = await response.json();
      setLeads(data.leads || []);
    } catch (error) {
      console.error("Error fetching leads:", error);
      toast.error("Failed to load investor leads");
    } finally {
      setLoading(false);
    }
  };

  const updateLead = async (leadId, updates) => {
    try {
      const token = localStorage.getItem("sterilemate_token");
      const response = await fetch(`${API}/investor-leads/${leadId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) throw new Error("Failed to update lead");
      
      toast.success("Lead updated");
      fetchLeads();
    } catch (error) {
      console.error("Error updating lead:", error);
      toast.error("Failed to update lead");
    }
  };

  const filteredLeads = leads.filter(lead => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      lead.full_name?.toLowerCase().includes(search) ||
      lead.email?.toLowerCase().includes(search) ||
      lead.company?.toLowerCase().includes(search)
    );
  });

  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === "new").length,
    inProgress: leads.filter(l => ["contacted", "meeting_scheduled", "in_discussions"].includes(l.status)).length,
    converted: leads.filter(l => l.status === "closed_won").length
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { 
      month: "short", 
      day: "numeric", 
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Investor Leads</h1>
          <p className="text-slate-600">Track and manage investor portal visitors</p>
        </div>
        <Button onClick={fetchLeads} variant="outline" className="gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Quick Access — Admin direct entry + Frost Bank share links */}
      <QuickAccessPanel />

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              <p className="text-sm text-slate-500">Total Leads</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.new}</p>
              <p className="text-sm text-slate-500">New (Uncontacted)</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.inProgress}</p>
              <p className="text-sm text-slate-500">In Progress</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Target className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{stats.converted}</p>
              <p className="text-sm text-slate-500">Converted</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search by name, email, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-slate-200 rounded-lg bg-white text-slate-700"
        >
          <option value="">All Statuses</option>
          {Object.entries(statusConfig).map(([key, config]) => (
            <option key={key} value={key}>{config.label}</option>
          ))}
        </select>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-3" />
            <p className="text-slate-500">Loading leads...</p>
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No investor leads yet</p>
            <p className="text-sm text-slate-400 mt-1">Leads will appear here when investors access your portal</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Contact</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Company</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Type</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Source</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Status</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">Accessed</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map((lead) => {
                const StatusIcon = statusConfig[lead.status]?.icon || Clock;
                return (
                  <tr 
                    key={lead._id} 
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setExpandedLead(expandedLead === lead._id ? null : lead._id)}
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white font-semibold">
                          {lead.full_name?.charAt(0) || "?"}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{lead.full_name}</p>
                          <p className="text-sm text-slate-500">{lead.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <p className="font-medium text-slate-900">{lead.company}</p>
                      <p className="text-sm text-slate-500">{lead.role}</p>
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-lg text-sm">
                        {investorTypeLabels[lead.investor_type] || lead.investor_type}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600">
                      {lead.how_heard?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || "Not specified"}
                    </td>
                    <td className="py-3 px-4">
                      <select
                        value={lead.status || "new"}
                        onChange={(e) => {
                          e.stopPropagation();
                          updateLead(lead._id, { status: e.target.value });
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className={`px-2 py-1 rounded-lg text-sm border ${statusConfig[lead.status]?.color || statusConfig.new.color}`}
                      >
                        {Object.entries(statusConfig).map(([key, config]) => (
                          <option key={key} value={key}>{config.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-500">
                      {formatDate(lead.created_at)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <a
                        href={`mailto:${lead.email}?subject=AskIsaac%20Investment%20Opportunity&body=Hi%20${lead.full_name?.split(" ")[0]},%0A%0AThank%20you%20for%20your%20interest%20in%20AskIsaac.%0A%0A`}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                      >
                        <Mail className="w-4 h-4" />
                        Email
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
